// ORCA360 - Transcript List Component
// Displays all available transcripts for a session (both transcribed and pre-uploaded)
// Supports multi-select and ordering for combining transcripts

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { formatDateISO } from '../lib/dateUtils';
import {
  sortTranscripts,
  getTranscriptTypeLabel,
  type TranscriptForCombine,
} from '../lib/transcriptCombination';

interface Transcript extends TranscriptForCombine {
  session_id: string;
  file_id: string | null;
  word_count: number;
  duration_seconds: number | null;
  processing_status: string;
  created_at: string;
  // File information (if associated with a file)
  file_type?: string | null;
  // Combined transcript metadata
  is_combined?: boolean;
  source_transcript_ids?: string[];
  // Archive status
  is_archived?: boolean;
}

interface TranscriptListProps {
  sessionId: string;
  onViewTranscript: (transcriptId: string) => void;
  onTranscriptSelect?: (transcriptId: string) => void;
  onProcessTranscript?: (transcriptId: string) => void;
  onCombineTranscripts?: (transcriptIds: string[]) => Promise<void>;
  selectedTranscriptId?: string;
  refreshTrigger?: number;
  processingTranscriptId?: string;
}

export default function TranscriptList({
  sessionId,
  onViewTranscript,
  onTranscriptSelect,
  onProcessTranscript,
  onCombineTranscripts,
  selectedTranscriptId,
  refreshTrigger,
  processingTranscriptId,
}: TranscriptListProps) {
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Multi-select state
  const [selectedForCombine, setSelectedForCombine] = useState<string[]>([]);
  const [customOrder, setCustomOrder] = useState<string[] | null>(null);
  const [isCombining, setIsCombining] = useState(false);

  const loadTranscripts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load transcripts with associated file information and combined metadata
      const { data, error: fetchError } = await supabase
        .from('transcripts')
        .select(
          `
          id,
          session_id,
          file_id,
          word_count,
          duration_seconds,
          processing_status,
          created_at,
          is_combined,
          source_transcript_ids,
          is_archived,
          files:file_id (
            file_name,
            file_type
          )
        `
        )
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Transform data to flatten file information
      const transformedTranscripts = (data || []).map((transcript) => {
        // Handle files as either object or array (Supabase can return either)
        const fileData = Array.isArray(transcript.files)
          ? transcript.files[0]
          : transcript.files;

        return {
          ...transcript,
          file_name: fileData?.file_name || null,
          file_type: fileData?.file_type || null,
          files: undefined, // Remove nested object
        };
      });

      setTranscripts(transformedTranscripts);
      console.log(`[TranscriptList] Loaded ${transformedTranscripts.length} transcripts`);
    } catch (err) {
      console.error('[TranscriptList] Error loading transcripts:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadTranscripts();

    // Set up real-time subscription for transcript updates
    const channel = supabase
      .channel(`transcripts-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transcripts',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          console.log(
            '[TranscriptList] Transcript change detected:',
            payload.eventType,
            payload.new
          );
          setTimeout(() => {
            loadTranscripts();
          }, 100);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, loadTranscripts]);

  // Watch for refresh trigger
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      console.log('[TranscriptList] Refresh triggered by parent component');
      loadTranscripts();
    }
  }, [refreshTrigger, loadTranscripts]);

  // Get transcripts selected for combining in order
  const orderedSelectedTranscripts = useMemo(() => {
    const selected = transcripts.filter((t) => selectedForCombine.includes(t.id));
    if (customOrder) {
      // Use custom order if set
      return customOrder
        .map((id) => selected.find((t) => t.id === id))
        .filter(Boolean) as Transcript[];
    }
    // Use auto-sorted order
    return sortTranscripts(selected);
  }, [transcripts, selectedForCombine, customOrder]);

  // Toggle transcript selection for combining
  const toggleSelectForCombine = (transcriptId: string) => {
    setSelectedForCombine((prev) => {
      if (prev.includes(transcriptId)) {
        // Remove from selection
        const newSelection = prev.filter((id) => id !== transcriptId);
        // Also remove from custom order if set
        if (customOrder) {
          setCustomOrder(customOrder.filter((id) => id !== transcriptId));
        }
        return newSelection;
      } else {
        // Add to selection
        return [...prev, transcriptId];
      }
    });
  };

  // Move transcript up in combine order
  const moveUp = (index: number) => {
    if (index === 0) return;
    const ids = customOrder || orderedSelectedTranscripts.map((t) => t.id);
    const newOrder = [...ids];
    const temp = newOrder[index - 1];
    newOrder[index - 1] = newOrder[index]!;
    newOrder[index] = temp!;
    setCustomOrder(newOrder);
  };

  // Move transcript down in combine order
  const moveDown = (index: number) => {
    const ids = customOrder || orderedSelectedTranscripts.map((t) => t.id);
    if (index === ids.length - 1) return;
    const newOrder = [...ids];
    const temp = newOrder[index];
    newOrder[index] = newOrder[index + 1]!;
    newOrder[index + 1] = temp!;
    setCustomOrder(newOrder);
  };

  // Reset to auto order
  const resetOrder = () => {
    setCustomOrder(null);
  };

  // Handle combine action
  const handleCombine = async () => {
    if (!onCombineTranscripts || orderedSelectedTranscripts.length < 2) return;

    try {
      setIsCombining(true);
      const orderedIds = orderedSelectedTranscripts.map((t) => t.id);
      await onCombineTranscripts(orderedIds);

      // Clear selection after successful combine
      setSelectedForCombine([]);
      setCustomOrder(null);
    } catch (err) {
      console.error('[TranscriptList] Error combining transcripts:', err);
    } finally {
      setIsCombining(false);
    }
  };

  const getTranscriptIcon = (transcript: Transcript): string => {
    if (transcript.is_combined) return '🔗'; // Combined transcript
    if (transcript.file_type === 'text') return '📄'; // Pre-transcribed text
    if (transcript.file_type === 'document') return '📎'; // Pre-transcribed document
    return '📄'; // Transcripts are always text content
  };

  const getTranscriptLabel = (transcript: Transcript): string => {
    if (transcript.is_combined) {
      return `Combined Transcript (${transcript.source_transcript_ids?.length || 0} sources)`;
    }
    if (transcript.file_name) {
      // For audio-sourced transcripts, show "Transcript of <audio>" since
      // the transcript text lives in the DB, not as a separate file
      if (transcript.file_type === 'audio') {
        return `Transcript of ${transcript.file_name}`;
      }
      // For text/document uploads, the file IS the transcript
      return transcript.file_name;
    }
    if (transcript.file_type === 'audio') {
      return 'Audio Transcript';
    }
    return `Manual Transcript ${transcript.id.slice(0, 8)}`;
  };

  const getTranscriptSubtitle = (transcript: Transcript): string => {
    const parts = [];

    if (transcript.word_count) {
      parts.push(`${transcript.word_count.toLocaleString()} words`);
    }

    if (transcript.duration_seconds) {
      const minutes = Math.floor(transcript.duration_seconds / 60);
      const seconds = transcript.duration_seconds % 60;
      parts.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }

    parts.push(formatDateISO(transcript.created_at));

    return parts.join(' • ');
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Archive toggle state
  const [showArchived, setShowArchived] = useState(false);

  // Filter archived transcripts
  const visibleTranscripts = useMemo(() => {
    if (showArchived) return transcripts;
    return transcripts.filter(t => !t.is_archived);
  }, [transcripts, showArchived]);

  const archivedCount = useMemo(() => {
    return transcripts.filter(t => t.is_archived).length;
  }, [transcripts]);

  // Filter out combined transcripts for combination selection
  const combinableTranscripts = visibleTranscripts.filter(
    (t) => !t.is_combined && !t.is_archived && t.processing_status === 'completed'
  );
  const showCombineUI = combinableTranscripts.length >= 2;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-sm text-steel">Loading transcripts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700 font-semibold">Failed to load transcripts</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadTranscripts}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (transcripts.length === 0) {
    return (
      <div className="bg-white border border-steel/20 rounded-lg p-6">
        <div className="text-center">
          <div className="text-4xl mb-3">📝</div>
          <h3 className="text-lg font-semibold text-navy mb-2">No Transcripts Available</h3>
          <p className="text-steel text-sm">
            Upload an audio file to transcribe, or upload a pre-transcribed text file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-4 border-b border-steel/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-navy">
              Available Transcripts ({visibleTranscripts.length})
            </h3>
            <p className="text-sm text-steel mt-1">
              {showCombineUI
                ? 'Select transcripts to combine, or click one to use for assessment'
                : 'Select a transcript to view or use for assessment generation'}
            </p>
          </div>
          {archivedCount > 0 && (
            <label className="flex items-center gap-2 text-sm text-steel cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4 text-amber-600 border-gray-300 rounded"
              />
              Show archived ({archivedCount})
            </label>
          )}
        </div>
      </div>

      {/* Combine UI - shows when 2+ transcripts are selected */}
      {selectedForCombine.length >= 2 && onCombineTranscripts && (
        <div className="bg-amber-50 border-b border-amber-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-amber-800">
              Combine {selectedForCombine.length} Transcripts
            </h4>
            <div className="flex items-center space-x-2">
              {customOrder && (
                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                  Custom order
                </span>
              )}
              <button
                onClick={resetOrder}
                className="text-xs text-amber-700 hover:text-amber-900 underline"
              >
                Reset to auto
              </button>
            </div>
          </div>
          <p className="text-sm text-amber-700 mb-3">
            Order determines how transcripts are combined. Timestamps will be adjusted.
          </p>

          {/* Ordered list with up/down buttons */}
          <div className="space-y-2 mb-4">
            {orderedSelectedTranscripts.map((transcript, index) => (
              <div
                key={transcript.id}
                className="flex items-center gap-2 bg-white rounded-md p-2 border border-amber-200"
              >
                <div className="flex flex-col">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className={`text-xs px-1 ${
                      index === 0
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-amber-600 hover:text-amber-800'
                    }`}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === orderedSelectedTranscripts.length - 1}
                    className={`text-xs px-1 ${
                      index === orderedSelectedTranscripts.length - 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-amber-600 hover:text-amber-800'
                    }`}
                  >
                    ▼
                  </button>
                </div>
                <span className="w-6 h-6 flex items-center justify-center bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
                  {index + 1}
                </span>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                  {getTranscriptTypeLabel(transcript)}
                </span>
                <span className="text-sm text-navy truncate flex-1">
                  {getTranscriptLabel(transcript)}
                </span>
                <span className="text-xs text-steel">
                  {transcript.word_count?.toLocaleString()} words
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setSelectedForCombine([]);
                setCustomOrder(null);
              }}
              className="px-3 py-1.5 text-sm text-amber-700 hover:text-amber-900"
            >
              Cancel
            </button>
            <button
              onClick={handleCombine}
              disabled={isCombining}
              className={`px-4 py-2 rounded-md font-semibold text-sm ${
                isCombining
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {isCombining ? (
                <>
                  <span className="inline-block animate-spin mr-1">⏳</span>
                  Combining...
                </>
              ) : (
                <>🔗 Combine Transcripts</>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate/10">
        {visibleTranscripts.map((transcript) => {
          const isSelectedForCombine = selectedForCombine.includes(transcript.id);
          const canCombine =
          !transcript.is_combined && !transcript.is_archived && transcript.processing_status === 'completed';
          const isTranscriptionRunning =
            transcript.processing_status === 'processing' ||
            transcript.processing_status === 'pending' ||
            processingTranscriptId === transcript.id;

          return (
            <div
              key={transcript.id}
              className={`p-4 hover:bg-ice/50 transition-colors cursor-pointer ${
                selectedTranscriptId === transcript.id
                  ? 'bg-orca-elevate/5 border-l-4 border-orca-elevate'
                  : ''
              } ${transcript.is_archived ? 'opacity-60' : ''}`}
              onClick={() => onTranscriptSelect?.(transcript.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Checkbox for combining (only for completed, non-combined, non-archived transcripts) */}
                  {showCombineUI && canCombine && onCombineTranscripts && (
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={isSelectedForCombine}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectForCombine(transcript.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                        title="Select for combining"
                      />
                    </div>
                  )}

                  <span className="text-2xl mt-1">{getTranscriptIcon(transcript)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-navy truncate">
                        {getTranscriptLabel(transcript)}
                      </h4>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                          transcript.processing_status
                        )}`}
                      >
                        {transcript.processing_status.toUpperCase()}
                      </span>
                      {transcript.is_combined && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 border border-purple-300">
                          COMBINED
                        </span>
                      )}
                      {transcript.is_archived && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-600 border border-gray-300">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-steel">{getTranscriptSubtitle(transcript)}</p>

                    {/* Source information */}
                    <div className="mt-2 flex items-center space-x-4 text-xs text-steel/70">
                      {transcript.is_combined && (
                        <span className="flex items-center space-x-1">
                          <span>🔗</span>
                          <span>
                            Combined from {transcript.source_transcript_ids?.length || 0}{' '}
                            transcripts
                          </span>
                        </span>
                      )}
                      {!transcript.is_combined && transcript.file_type === 'audio' && (
                        <span className="flex items-center space-x-1">
                          <span>🎤</span>
                          <span>Transcribed from audio</span>
                        </span>
                      )}
                      {!transcript.is_combined && transcript.file_type === 'text' && (
                        <span className="flex items-center space-x-1">
                          <span>📄</span>
                          <span>Pre-transcribed text</span>
                        </span>
                      )}
                      {!transcript.is_combined && transcript.file_type === 'document' && (
                        <span className="flex items-center space-x-1">
                          <span>📎</span>
                          <span>Pre-transcribed document</span>
                        </span>
                      )}
                      {!transcript.is_combined && !transcript.file_id && (
                        <span className="flex items-center space-x-1">
                          <span>✍️</span>
                          <span>Manual transcript</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {/* Show Process button for pending transcripts */}
                  {transcript.processing_status === 'pending' && onProcessTranscript && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onProcessTranscript(transcript.id);
                      }}
                      disabled={processingTranscriptId === transcript.id}
                      className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
                        processingTranscriptId === transcript.id
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                      title="Process this transcript through the full pipeline (segments + events)"
                    >
                      {processingTranscriptId === transcript.id ? (
                        <>
                          <span className="inline-block animate-spin mr-1">⏳</span>
                          Processing...
                        </>
                      ) : (
                        <>🚀 Process</>
                      )}
                    </button>
                  )}

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTranscript(transcript.id);
                    }}
                    disabled={isTranscriptionRunning}
                    className={`px-3 py-1 text-sm font-medium transition-colors ${
                      isTranscriptionRunning
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-orca-elevate hover:text-orca-elevate/80'
                    }`}
                    title={
                      isTranscriptionRunning
                        ? 'Transcription in progress - please wait'
                        : 'View transcript in editor'
                    }
                  >
                    👁️ View
                  </button>
                  {selectedTranscriptId === transcript.id && (
                    <div className="text-orca-elevate text-sm font-medium">✓ Selected</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
